import { Card, CardBody, CardHeader, CardTitle } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import { useStageAging } from '../../data/usePipelinesSource.ts'
import type { AgingBucket } from '../../types.ts'

const BUCKET_COLOR: Record<AgingBucket, string> = {
  '0-3d': '#10b981', // emerald
  '4-7d': '#22c55e', // green-light
  '8-14d': '#f59e0b', // amber
  '15-30d': '#f97316', // orange
  '30d+': '#ef4444', // red
}

export function AgingDistribution() {
  const { t } = useTranslation()
  const buckets = useStageAging()

  const total = buckets.reduce((s, b) => s + b.count, 0)

  return (
    <Card className="rounded-xl border-border shadow-none">
      <CardHeader className="border-border/20 py-2.5">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
          {t('pages.pipelines.side.agingTitle', { defaultValue: 'Aging distribution' })}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-2 py-3">
        {total === 0 ? (
          <span className="text-xs text-muted">
            {t('pages.pipelines.side.empty', { defaultValue: 'Nothing to show.' })}
          </span>
        ) : (
          <>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-border/40">
              {buckets.map((b) =>
                b.count === 0 ? null : (
                  <span
                    key={b.bucket}
                    className="h-full"
                    style={{
                      width: `${(b.count / total) * 100}%`,
                      backgroundColor: BUCKET_COLOR[b.bucket],
                    }}
                    aria-label={`${b.bucket}: ${b.count}`}
                  />
                ),
              )}
            </div>
            <ul className="grid grid-cols-1 gap-1.5">
              {buckets.map((b) => (
                <li key={b.bucket} className="flex items-center gap-2 text-xs">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: BUCKET_COLOR[b.bucket] }}
                    aria-hidden="true"
                  />
                  <span className="text-muted">{b.bucket}</span>
                  <span className="ml-auto font-mono text-sm font-semibold tabular-nums text-foreground">
                    {b.count}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardBody>
    </Card>
  )
}
